import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import WizardShell from './WizardShell';
import MediaPickStep from './steps/MediaPickStep';
import MusicPickStep from './steps/MusicPickStep';
import ScreensPickStep from './steps/ScreensPickStep';
import SaveStep from './steps/SaveStep';
import ScopeChoice from './steps/ScopeChoice';
import { useStartCafeVibes, useCafeWizardSlots } from '../../../queries/cafeWizard';
import { useCustomToast } from '../../Contexts/ToastContext';

// slot key -> {template_key, title}. 'now' has no template_key at all — see
// SaveStep's own note and CafeWizardService::build()'s $ephemeral handling.
const SLOT_META = {
  now:  { templateKey: null,               titleKey: null },
  morn: { templateKey: 'vibes_morning',    titleKey: 'slot_morn_title', emoji: '🌅' },
  noon: { templateKey: 'vibes_afternoon',  titleKey: 'slot_noon_title', emoji: '☀️' },
  eve:  { templateKey: 'vibes_evening',    titleKey: 'slot_eve_title',  emoji: '🌆' },
};

/**
 * ☕ Café Vibes / 🎬 Silent Display wizard.
 *
 * mode='vibes'  → Media → Music → Screens → Save  (photos/videos + optional music)
 * mode='silent' → Media → Screens → Save          (everything muted, no music step)
 *
 * On finish it POSTs /cafe/wizard/vibes, which builds a re-playable Show and
 * pushes it live. The words layer / campaign / publish never appear.
 *
 * ⚠️ THE SAVE STEP REPLACES THE OLD templateKey PROP (2026-09). Where to
 * save — "شغّله الآن فقط" or one of the three daypart buttons — used to be
 * decided on the PICKER, before the owner had built anything. It is now
 * asked at the END, where the owner can see each slot's real occupancy and
 * knows what they're actually placing. Props: mode:'vibes'|'silent',
 * onExit:()=>void, onDone:()=>void.
 */
export default function CafeVibesWizard({ mode = 'vibes', onExit, onDone }) {
  const { t } = useTranslation();
  const { showToast } = useCustomToast();
  const silent = mode === 'silent';

  const [stepIndex, setStepIndex] = useState(0);
  const [mediaIds, setMediaIds] = useState([]);
  const [musicIds, setMusicIds] = useState([]);
  const [deviceIds, setDeviceIds] = useState([]);
  const [slot, setSlot] = useState(null);
  // the server's 409 needs_scope_choice answer: the slot's show is also used by screens outside this selection
  const [choice, setChoice] = useState(null);
  // the person's answer when the slot's show is shared with other screens: null (not chosen yet) | 'all' | 'own'. Choosing never saves by itself.
  const [scope, setScope] = useState(null);

  const start = useStartCafeVibes();

  const meta = silent
    ? { icon: '🎬', title: t('cafe_wizard.silent.title'), subtitle: t('cafe_wizard.silent.subtitle'),
        steps: [t('cafe_wizard.step_media'), t('cafe_wizard.step_screens'), t('cafe_wizard.step_save')] }
    : { icon: '☕', title: t('cafe_wizard.vibes.title'), subtitle: t('cafe_wizard.vibes.subtitle'),
        steps: [t('cafe_wizard.step_media'), t('cafe_wizard.step_music'), t('cafe_wizard.step_screens'), t('cafe_wizard.step_save')] };

  const isLast = stepIndex === meta.steps.length - 1;
  const isSaveStep = isLast; // Save is always the final step in both flows.
  const isScreensStep = stepIndex === meta.steps.length - 2;

  // the save step's real occupancy (same query SaveStep reads): is the chosen slot's show shared with screens outside the selection?
  const slotsQ = useCafeWizardSlots(deviceIds, isSaveStep);
  const slotKey = SLOT_META[slot]?.templateKey ?? null;
  const sharedChosen = slotKey ? (slotsQ.data?.[slotKey]?.shared ?? null) : null;
  // a stale answer must not follow a different slot / selection
  useEffect(() => { setScope(null); }, [slot, deviceIds]);

  const canNext = useMemo(() => {
    if (stepIndex === 0) return mediaIds.length > 0;
    if (isSaveStep) return !!slot && (!slotKey || !slotsQ.isLoading) && (!sharedChosen || !!scope);
    if (isScreensStep) return deviceIds.length > 0;
    return true; // music step is optional
  }, [stepIndex, isSaveStep, isScreensStep, mediaIds, deviceIds, slot, slotKey, slotsQ.isLoading, sharedChosen, scope]);

  const submit = useCallback((chosenScope) => {
    const slotMeta = SLOT_META[slot];
    const name = slotMeta?.titleKey
      ? `${slotMeta.emoji} ${t(`cafe_wizard.${slotMeta.titleKey}`)}`
      : (silent ? t('cafe_wizard.silent.title') : t('cafe_wizard.vibes.title'));

    start.mutate(
      {
        name,
        media_ids: mediaIds,
        music_media_ids: silent ? [] : musicIds,
        silent,
        device_ids: deviceIds,
        ...(slotMeta?.templateKey ? { template_key: slotMeta.templateKey } : {}),
        ephemeral: slot === 'now',
        scope: chosenScope || 'ask',
      },
      {
        onError: (err) => {
          if (err?.response?.status === 409 && err?.response?.data?.message === 'needs_scope_choice') { setChoice(err.response.data.data); setScope(null); }
        },
        onSuccess: (res) => {
          const names = (res?.data?.assigned_devices ?? []).map((d) => d.name).join(', ');
          showToast('success', name, t('cafe_wizard.started', { screens: names || '—' }));
          const copied = res?.data?.copied_layers;
          if (Array.isArray(copied) && copied.length) {
            showToast('info', name, t('cafe_wizard.content_copied_note', { layers: copied.join('، ') }));
          }
          onDone?.();
        },
      },
    );
  }, [start, silent, mediaIds, musicIds, deviceIds, slot, showToast, t, onDone]);


  const onBack = () => (choice ? (setChoice(null), setScope(null)) : stepIndex === 0 ? onExit?.() : setStepIndex((i) => i - 1));
  const onNext = () => (isLast ? submit(scope) : setStepIndex((i) => i + 1));

  return (
    <WizardShell
      icon={meta.icon} title={meta.title} subtitle={meta.subtitle}
      steps={meta.steps} stepIndex={stepIndex}
      canNext={choice ? !!scope : canNext} submitting={start.isLoading}
      nextLabel={isLast ? (slot === 'now' ? t('cafe_wizard.play_now_final') : t('cafe_wizard.save_and_play')) : undefined}
      onBack={onBack} onNext={onNext} onStepClick={setStepIndex}
    >
      {choice && <ScopeChoice choice={choice} value={scope} onChange={setScope} busy={start.isLoading} />}
      {!choice && stepIndex === 0 && <MediaPickStep value={mediaIds} onChange={setMediaIds} />}
      {!choice && !silent && stepIndex === 1 && <MusicPickStep value={musicIds} onChange={setMusicIds} />}
      {!choice && isScreensStep && <ScreensPickStep value={deviceIds} onChange={setDeviceIds} />}
      {!choice && isSaveStep && <SaveStep value={slot} onChange={setSlot} deviceIds={deviceIds} scope={scope} onScopeChange={setScope} />}
      {!choice && isSaveStep && <div className="fc-inote" dangerouslySetInnerHTML={{ __html: t('cafe_wizard.save_step_after_note') }} />}
    </WizardShell>
  );
}
